/**
 * The scan-package rules, client side — what a phone can check *before* it
 * spends a 100 MB upload on a refusal (card `t_8ae1d20d`/E2; the hub's own half
 * is `otto/src/lib/scanPackage.ts` + `otto/src/server/utils/scanPackageIntake.ts`).
 *
 * The point of this module is not to re-implement the hub. It is to make the
 * refusals the hub is *certain* to send — a name it will not store, a package
 * over its limits, a `scan.json` that is not a `torik.scan/v0` document — arrive
 * on the screen before the bytes leave the device, and to keep the two accepted
 * shapes honest at the same time:
 *
 *   - a package without `scan.json` is **accepted** by the hub (a room-level
 *     capture the app has not merged yet — the real capture-2 is exactly that
 *     case, `meta.json` + `room-1.*` only), so its absence is a *warning*, never
 *     an error;
 *   - a package without any geometry (no `.usdz`) is something the hub would
 *     store and nobody could render, so it is the one error the hub itself does
 *     not raise — "scan a room" is the point of the feature.
 *
 * Everything here is pure: no Capacitor, no DOM, no fetch. The caller holds the
 * copy (`views.scans.invalid.*` in the six catalogues) — this module answers
 * codes, because a rule is not a sentence and the sentences live in the locale
 * files (`otto-ui-development` rule 2).
 */

/** Files per package — the hub's own limit (`MAX_SCAN_FILES`). */
export const SCAN_PACKAGE_MAX_FILES = 64;
/** Total bytes per package — the hub's own limit (`MAX_SCAN_PACKAGE_BYTES`). */
export const SCAN_PACKAGE_MAX_BYTES = 100 * 1024 * 1024;
/** Longest file name the hub stores (`MAX_SCAN_FILE_NAME_CHARS`). */
export const SCAN_PACKAGE_MAX_NAME_CHARS = 255;
/** The merged document's name; the only file the hub contract-checks. */
export const SCAN_DOC_NAME = 'scan.json';
/** The app's own manifest of a capture directory. */
export const SCAN_META_NAME = 'meta.json';
/** The contract string a `scan.json` carries (`SCAN_CONTRACT`). */
export const SCAN_CONTRACT = 'torik.scan/v0';

/**
 * What a package picker offers. A capture directory is `*.scan.json` + the
 * `.usdz` geometry that is its whole product, plus the raw dumps, the plan the
 * pipeline renders and the GLB a viewer wants — so the accept list is those
 * families and nothing else (a `.zip` of a capture is not a package: the hub
 * stores files, and it does not unpack anything).
 */
export const SCAN_PACKAGE_ACCEPT = '.json,.usdz,.glb,.gltf,.png,.svg,.pdf,.obj,.ply';

/** A file as the picker (or a capture directory) hands it over. */
export interface ScanFileLike {
  name: string;
  size: number;
}

/** The verdicts a caller translates; one code, one sentence. */
export type ScanPackageIssueCode =
  | 'empty'
  | 'tooManyFiles'
  | 'fileTooLarge'
  | 'packageTooLarge'
  | 'badFileName'
  | 'duplicateName'
  | 'missingUsdz'
  | 'missingScanDoc'
  | 'badScanDoc';

export interface ScanPackageIssue {
  code: ScanPackageIssueCode;
  /** The file the verdict is about, when it is about one. */
  name?: string;
}

export interface ScanPackageValidation {
  /** `false` when the hub would refuse this package (the errors decide). */
  ok: boolean;
  errors: ScanPackageIssue[];
  /** Accepted by the hub, worth saying out loud. */
  warnings: ScanPackageIssue[];
}

/** Lower-cased extension, without the dot; `''` when the name has none. */
export function scanFileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * Is this the v0 document of a capture — `scan.json` (the merged one) or a
 * room's `room-N.scan.json`? The per-room files survive a merge that never ran,
 * so both are real.
 */
export function isScanDocName(name: string): boolean {
  if (name === SCAN_DOC_NAME) return true;
  return /^room-\d+\.scan\.json$/.test(name);
}

/** The `.usdz` geometry of a capture (per-room `room-N.usdz` included). */
export function isScanGeometryName(name: string): boolean {
  return scanFileExtension(name) === 'usdz';
}

/** The `.glb`/`.gltf` the scan pipeline renders from the geometry. */
export function isScanModelName(name: string): boolean {
  const extension = scanFileExtension(name);
  return extension === 'glb' || extension === 'gltf';
}

/**
 * The content type a file is stored with, by extension — the phone's copy of
 * the hub's table (`scanContentType`), because a `File` built from device bytes
 * carries none of its own.
 */
export function scanContentType(name: string): string {
  switch (scanFileExtension(name)) {
    case 'usdz':
      return 'model/vnd.usdz+zip';
    case 'usd':
    case 'usda':
      return 'model/vnd.usd';
    case 'glb':
      return 'model/gltf-binary';
    case 'gltf':
      return 'model/gltf+json';
    case 'json':
      return 'application/json';
    case 'png':
      return 'image/png';
    case 'svg':
      return 'image/svg+xml';
    case 'pdf':
      return 'application/pdf';
    case 'obj':
      return 'text/plain; charset=utf-8';
    default:
      return 'application/octet-stream';
  }
}

/**
 * The name the hub would refuse, or `null` when it stores it — the client's
 * copy of `sanitizeScanFileName`, and it refuses for the same reasons: a name
 * becomes a storage key and a URL path segment, so `/` and `\` are refused (a
 * package is flat), as are NUL and every other control character, `.`/`..`, a
 * leading dot, a trailing dot or space, and any leading or trailing whitespace.
 */
export function scanFileNameRefusal(name: string): ScanPackageIssueCode | null {
  if (typeof name !== 'string' || name.length === 0) return 'badFileName';
  if (name.length > SCAN_PACKAGE_MAX_NAME_CHARS) return 'badFileName';
  if (name.includes('/') || name.includes('\\')) return 'badFileName';
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(name)) return 'badFileName';
  if (name === '.' || name === '..') return 'badFileName';
  if (name.startsWith('.')) return 'badFileName';
  if (name !== name.trim()) return 'badFileName';
  if (/[. ]$/.test(name)) return 'badFileName';
  return null;
}

function megabytes(bytes: number): number {
  return Math.floor(bytes / (1024 * 1024));
}

/**
 * The whole verdict on a package, in the order the hub applies it: an empty
 * pick, the file count, a single file or the package too large, then the names,
 * then the two shape rules that are this client's own (geometry present, and a
 * `scan.json` that is the contract it claims to be — `scanDocContract` must
 * have been read by the caller, it cannot be guessed from a name).
 *
 * `maxBytes`/`maxFiles` are parameters so a test can drive the limits without
 * building a 100 MB package.
 */
export function validateScanPackage(
  files: ScanFileLike[],
  verdicts: { scanDocContract?: string | null } = {},
  limits: { maxFiles?: number; maxBytes?: number } = {},
): ScanPackageValidation {
  const maxFiles = limits.maxFiles ?? SCAN_PACKAGE_MAX_FILES;
  const maxBytes = limits.maxBytes ?? SCAN_PACKAGE_MAX_BYTES;
  const errors: ScanPackageIssue[] = [];
  const warnings: ScanPackageIssue[] = [];

  if (files.length === 0) {
    return { ok: false, errors: [{ code: 'empty' }], warnings };
  }
  if (files.length > maxFiles) {
    errors.push({ code: 'tooManyFiles' });
  }

  const seen = new Set<string>();
  let total = 0;
  for (const file of files) {
    total += Number.isFinite(file.size) ? file.size : 0;
    if (file.size > maxBytes) errors.push({ code: 'fileTooLarge', name: file.name });
    const refusal = scanFileNameRefusal(file.name);
    if (refusal) errors.push({ code: refusal, name: file.name });
    const key = file.name.toLowerCase();
    if (seen.has(key)) errors.push({ code: 'duplicateName', name: file.name });
    seen.add(key);
  }
  if (total > maxBytes) errors.push({ code: 'packageTooLarge' });

  if (!files.some((file) => isScanGeometryName(file.name))) {
    errors.push({ code: 'missingUsdz' });
  }
  // The hub's own verdict: only a package carrying `scan.json` itself has been
  // normalized, so its absence is the room-level warning it answers with
  // (measured on the live hub for the real capture-2, which carries
  // `room-1.scan.json` and still got the warning).
  const mergedDoc = files.find((file) => file.name === SCAN_DOC_NAME);
  if (!mergedDoc) {
    warnings.push({ code: 'missingScanDoc' });
  } else if (verdicts.scanDocContract !== SCAN_CONTRACT) {
    // A `scan.json` the hub would refuse with a 400 and a `details[]`; the bytes
    // never need to travel for that answer.
    errors.push({ code: 'badScanDoc' });
  }

  return { ok: errors.length === 0, errors, warnings };
}

/**
 * The `schema` a `scan.json` text declares, or `null` when the text is not a
 * JSON object with one. The caller reads the file's text (a browser `File`
 * carries it) and hands it here, so the verdict stays pure.
 */
export function scanDocContract(text: string): string | null {
  try {
    const parsed: unknown = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const schema = (parsed as { schema?: unknown }).schema;
    return typeof schema === 'string' ? schema : null;
  } catch {
    return null;
  }
}

/** The largest size, in MB, the sentences quote — one place, so they cannot drift. */
export function scanPackageMaxMegabytes(limits: { maxBytes?: number } = {}): number {
  return megabytes(limits.maxBytes ?? SCAN_PACKAGE_MAX_BYTES);
}

/** `file:///…/RoomScan/<id>/scan.json` → `scan.json`. */
export function scanFileNameOf(path: string): string {
  return path.split('/').pop() ?? path;
}

/**
 * The file names a capture directory holds, newest builds first:
 *
 *   1. `files` — the plugin's own listing of the directory
 *      (`RoomScanCaptureSummary.files`), which is the truth;
 *   2. when a build does not answer one, the basenames of the three paths the
 *      plugin does name (`scanPath`, `usdzPath`, `jsonPath`) plus `meta.json`,
 *      which every capture directory carries — enough to upload a package from
 *      a build that lists nothing.
 *
 * Duplicates are dropped, order is kept, and a name is never invented.
 */
export function capturePackageFileNames(capture: {
  files?: string[];
  scanPath?: string;
  usdzPath?: string;
  jsonPath?: string;
}): string[] {
  const listed = Array.isArray(capture.files) ? capture.files.filter((name) => typeof name === 'string' && name) : [];
  const named = [capture.scanPath, capture.usdzPath, capture.jsonPath].filter(
    (path): path is string => typeof path === 'string' && path.length > 0,
  );
  // Nothing listed and nothing named is a capture that cannot be read at all —
  // say so with an empty list rather than inventing a manifest.
  const candidates = listed.length ? listed : named.length ? [...named.map(scanFileNameOf), SCAN_META_NAME] : [];
  const seen = new Set<string>();
  const names: string[] = [];
  for (const name of candidates) {
    if (seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  return names;
}

/** Where a capture directory lives for `@capacitor/filesystem` (`Directory.Documents`). */
export function captureDirectoryPath(captureId: string, fileName: string): string {
  return `RoomScan/${captureId}/${fileName}`;
}
